import {
    ChangeResult,
    IActions,
    IOption,
    IOrderDetails,
    ISelectedOptionIds,
    sides,
    optionKinds,
    IProductDetail,
} from '../../../models/product-detail';
import { IPatient } from '../../../models/prosites';
import { getEarmoldOptions } from '../../../api/product-api';
import { ISession } from '../../../models/pro-sites';
import { CartItemTypes } from '../../common/constants';

//#region Rules

let rules: IActions[] = [];
let ruleTimeOptionAvailability: IOption[] = [];
let ruleTimeSelectedIds: ISelectedOptionIds = {
    rightSelectedIds: [],
    leftSelectedIds: [],
};
let ruleTimeSelectedSide: sides;

let orderstateSelectedIds: ISelectedOptionIds = {
    rightSelectedIds: [],
    leftSelectedIds: [],
};

export const findOption = (
    id: number | string,
    options: IOption[]
): IOption | null => {
    if (options) {
        for (let i = 0; i < options.length; i++) {
            const item: IOption = options[i];
            if (
                item.name === id ||
                item.english === id ||
                (item.id === id && item.kind)
            ) {
                return item;
            } else if (item.items) {
                const subItem: IOption | null = findOption(id, item.items);
                if (subItem) return subItem;
            } else if (item.id === id) {
                return item;
            }
        }
    }
    return null;
};

const isSelected = (id: number, side: string) => {
    if (side === sides.right) {
        return ruleTimeSelectedIds.rightSelectedIds.includes(id);
    }
    if (side === sides.left) {
        return ruleTimeSelectedIds.leftSelectedIds.includes(id);
    }
    return false;
};

const hasChanged = (option: IOption, side: string) => {
    if (option) {
        // Using logical XOR operator(!=) for this comparision
        if (side === sides.right) {
            // According to old code the initial selection was undefined and was returning true when compared.
            if (
                orderstateSelectedIds.rightSelectedIds.length === 0 &&
                ![599, 600].includes(option.id)
            ) {
                return true;
            } else {
                return (
                    ruleTimeSelectedIds.rightSelectedIds.includes(option.id) !==
                    orderstateSelectedIds.rightSelectedIds.includes(option.id)
                );
            }
        }
        if (side === sides.left) {
            if (
                orderstateSelectedIds.leftSelectedIds.length === 0 &&
                ![599, 600].includes(option.id)
            ) {
                return true;
            } else {
                return (
                    ruleTimeSelectedIds.leftSelectedIds.includes(option.id) !==
                    orderstateSelectedIds.leftSelectedIds.includes(option.id)
                );
            }
        }
    }
    return false;
};
export const parseSide = (side: string | null) => {
    if (!side) return null;
    if (side.charAt(0).toUpperCase() === 'B') return sides.binaural;
    if (side.charAt(0).toUpperCase() === 'R') return sides.right;
    return sides.left;
};

export const isAvailable = (option: IOption, side: string) => {
    if (option) {
        if (side === sides.right) {
            return option.isAvailableRight;
        } else if (side === sides.left) {
            return option.isAvailableLeft;
        } else if (side === sides.binaural) {
            return option.isAvailableRight && option.isAvailableLeft;
        }
    }
};

const isActivatable = (id: number, side: string) => {
    const abc = rules.some((x) => {
        return (
            (x.incompatible === id &&
                ((x.hidesFrom || []).some((opt) => {
                    const item = findOption(opt, ruleTimeOptionAvailability);
                    if (item) {
                        return isSelected(item.id, side);
                    } else {
                        return false;
                    }
                }) ||
                    (x.showIf &&
                        x.showIf.every((opt) => {
                            return !isSelected(
                                findOption(opt, ruleTimeOptionAvailability)!.id,
                                side
                            );
                        })))) ||
            ((x.hides || []).includes(id) &&
                isSelected(
                    findOption(x.incompatible, ruleTimeOptionAvailability)!.id,
                    side
                )) ||
            ((x.deactivates || []).includes(id) &&
                ((x.unselected &&
                    !isSelected(
                        findOption(x.unselected, ruleTimeOptionAvailability)!
                            .id,
                        side
                    )) ||
                    (x.selected &&
                        isSelected(
                            findOption(x.selected, ruleTimeOptionAvailability)!
                                .id,
                            side
                        )))) ||
            (x.incompatible === id &&
                (x.requires || []).some((opt) => {
                    return !isSelected(
                        findOption(opt, ruleTimeOptionAvailability)!.id,
                        side
                    );
                }))
        );
    });

    return !abc;
};

const updateOptionsState = (option: IOption) => {
    const temp = ruleTimeOptionAvailability;
    if (option) {
        temp!.forEach(function iter(a) {
            if (option.id === a.id) {
                a = option;
            }
            Array.isArray(a.items) && a.items.forEach(iter);
        });

        ruleTimeOptionAvailability = temp;
    }
};

const addRemoveFromOptions = (option: IOption, side: string, add: boolean) => {
    if (option) {
        if (side === sides.right) {
            option.isAvailableRight = add;
        }
        if (side === sides.left) {
            option.isAvailableLeft = add;
        }
    }

    updateOptionsState(option);
};

export const loadOptionsAndRules = (options: IOption[]) => {
    if (!options || options.length === 0) {
        return [];
    }

    const cloned: IOption[] = JSON.parse(JSON.stringify(options));

    //Set the kind of option
    cloned.forEach((optionCategory) => {
        if (optionCategory.items.length > 0) {
            optionCategory.items.forEach((optiontype) => {
                optiontype.kind = optionKinds[optiontype.groupType!];

                if (optiontype.items.length > 0) {
                    optiontype.items.forEach((option) => {
                        option.isAvailableLeft = true;
                        option.isAvailableRight = true;
                        if (optiontype.kind) {
                            let optionObject: IOption;
                            option.parent = {
                                ...optionObject!,
                                kind: optiontype.kind,
                                id: optiontype.id,
                                name: optiontype.name,
                            };
                        }
                    });
                }
            });
        }
    });

    //load Receiver Gain option goes here

    return cloned;
};

export const extractRules = (options: IOption[], rules: IActions[]) => {
    options.forEach((option) => {
        if (option.items) {
            extractRules(option.items, rules);
        } else {
            if (option.onSelect) {
                option.onSelect.selected = option.id;
                rules.push(option.onSelect);
            }
            if (option.onUnselect) {
                option.onUnselect.unselected = option.id;
                rules.push(option.onUnselect);
            }
            if (option.incompatible) {
                option.incompatible.incompatible = option.id;
                rules.push(option.incompatible);
            }
        }
    });
};

export const extractDefaultOptions = (
    option: IOption,
    side: string,
    selectedIds: ISelectedOptionIds,
    needsValue?: boolean
) => {
    if (!option) {
        return;
    }
    let hasDefault = false;
    let firstId: number | null = null;
    let lastId: number | null = null;
    for (let i = 0; i < option.items.length; i++) {
        const item = option.items[i];
        if (item.items) {
            extractDefaultOptions(item, side, selectedIds, needsValue);
        } else if (isAvailable(item, side)) {
            if (firstId === null) {
                firstId = item.id;
            }
            lastId = item.id;
            if (item.isDefault) {
                if (
                    side !== sides.left &&
                    !selectedIds.rightSelectedIds.includes(item.id)
                ) {
                    selectedIds.rightSelectedIds.push(item.id);
                }
                if (
                    side !== sides.right &&
                    !selectedIds.leftSelectedIds.includes(item.id)
                ) {
                    selectedIds.leftSelectedIds.push(item.id);
                }
                hasDefault = true;
            }
        }
    }
    if (['select', 'dual-select'].includes(option.kind)) {
        needsValue = true;
    }

    if (['select-nd', 'dual-select-nd'].includes(option.kind)) {
        needsValue = false;
    }

    if (firstId === lastId && option.id !== 85 && option.id !== 41) {
        needsValue = true; // Group Id 85 for Include CROS Earmold Options
    }

    if (['multi', 'dual-multi', 'dual-single-nd'].includes(option.kind)) {
        needsValue = false;
    }

    if (!hasDefault && needsValue) {
        if (firstId) {
            if (
                side !== sides.left &&
                !selectedIds.rightSelectedIds.includes(firstId)
            ) {
                selectedIds.rightSelectedIds.push(firstId);
            }
            if (
                side !== sides.right &&
                !selectedIds.leftSelectedIds.includes(firstId)
            ) {
                selectedIds.leftSelectedIds.push(firstId);
            }
        }
    }
};

export const addIncludedAccessoriesDisplay = (
    option: IOption,
    side: string,
    selectedIds: ISelectedOptionIds
) => {
    if (!option) {
        return;
    }

    for (let i = 0; i < option.items.length; i++) {
        const item = option.items[i];

        if (item.items) {
            addIncludedAccessoriesDisplay(item, side, selectedIds);
        } else if (option.name === 'Included Accessories (display)') {
            if (
                side === sides.right &&
                !selectedIds.rightSelectedIds.includes(item.id)
            ) {
                selectedIds.rightSelectedIds.push(item.id);
            }
            if (
                side === sides.left &&
                !selectedIds.leftSelectedIds.includes(item.id)
            ) {
                selectedIds.leftSelectedIds.push(item.id);
            }
        }
    }
};

const selectDefaultsForOptionType = (
    optiontype: IOption,
    side: string,
    needsValue?: boolean
) => {
    extractDefaultOptions(
        findOption(optiontype.name, ruleTimeOptionAvailability)!,
        optiontype.kind === 'single' ? ruleTimeSelectedSide : side,
        {
            rightSelectedIds: ruleTimeSelectedIds.rightSelectedIds,
            leftSelectedIds: ruleTimeSelectedIds.leftSelectedIds,
        },
        needsValue
    );
};

const resetGroup = (option: IOption, side: string) => {
    if (!option) return;
    findOption(option.parent!.id, ruleTimeOptionAvailability)!.items.forEach(
        (x) => {
            if (x !== option) {
                if (option.parent!.kind === 'single') {
                    if (ruleTimeSelectedIds.rightSelectedIds.includes(x.id)) {
                        ruleTimeSelectedIds.rightSelectedIds.splice(
                            ruleTimeSelectedIds.rightSelectedIds.indexOf(x.id),
                            1
                        );
                    }
                    if (ruleTimeSelectedIds.leftSelectedIds.includes(x.id)) {
                        ruleTimeSelectedIds.leftSelectedIds.splice(
                            ruleTimeSelectedIds.leftSelectedIds.indexOf(x.id),
                            1
                        );
                    }
                } else {
                    if (side === sides.right) {
                        if (
                            ruleTimeSelectedIds.rightSelectedIds.includes(x.id)
                        ) {
                            ruleTimeSelectedIds.rightSelectedIds.splice(
                                ruleTimeSelectedIds.rightSelectedIds.indexOf(
                                    x.id
                                ),
                                1
                            );
                        }
                    }
                    if (side === sides.left) {
                        if (
                            ruleTimeSelectedIds.leftSelectedIds.includes(x.id)
                        ) {
                            ruleTimeSelectedIds.leftSelectedIds.splice(
                                ruleTimeSelectedIds.leftSelectedIds.indexOf(
                                    x.id
                                ),
                                1
                            );
                        }
                    }
                }
            }
        }
    );
};

const addRemoveFromSelects = (option: IOption, side: string, add: boolean) => {
    if (option) {
        if (add) {
            //add to selected options

            if (
                option.parent &&
                ['select', 'select-nd', 'single', 'single, inline'].includes(
                    option.parent.kind
                )
            ) {
                resetGroup(option, side);
            } else if (
                option.parent &&
                [
                    'dual-select',
                    'dual-select-nd',
                    'dual-single',
                    'dual-single-nd',
                ].includes(option.parent.kind)
            ) {
                resetGroup(option, side);
            }
            if (
                option.parent &&
                ['select', 'select-nd'].includes(option.parent.kind)
            ) {
                if (!ruleTimeSelectedIds.rightSelectedIds.includes(option.id))
                    ruleTimeSelectedIds.rightSelectedIds.push(option.id);
                if (!ruleTimeSelectedIds.leftSelectedIds.includes(option.id))
                    ruleTimeSelectedIds.leftSelectedIds.push(option.id);
            }
            if (
                option.parent &&
                ['dual-select', 'dual-select-nd', 'single, inline'].includes(
                    option.parent.kind
                )
            ) {
                if (side === sides.right) {
                    if (
                        !ruleTimeSelectedIds.rightSelectedIds.includes(
                            option.id
                        )
                    )
                        ruleTimeSelectedIds.rightSelectedIds.push(option.id);
                }
                if (side === sides.left) {
                    if (
                        !ruleTimeSelectedIds.leftSelectedIds.includes(option.id)
                    )
                        ruleTimeSelectedIds.leftSelectedIds.push(option.id);
                }
            }
            if (option.parent && ['single'].includes(option.parent.kind)) {
                if (ruleTimeSelectedSide !== sides.left) {
                    if (
                        !ruleTimeSelectedIds.rightSelectedIds.includes(
                            option.id
                        )
                    )
                        ruleTimeSelectedIds.rightSelectedIds.push(option.id);
                }
                if (ruleTimeSelectedSide !== sides.right) {
                    if (
                        !ruleTimeSelectedIds.leftSelectedIds.includes(option.id)
                    )
                        ruleTimeSelectedIds.leftSelectedIds.push(option.id);
                }
            }
            if (side === sides.right) {
                if (!ruleTimeSelectedIds.rightSelectedIds.includes(option.id))
                    ruleTimeSelectedIds.rightSelectedIds.push(option.id);
            }
            if (side === sides.left) {
                if (!ruleTimeSelectedIds.leftSelectedIds.includes(option.id))
                    ruleTimeSelectedIds.leftSelectedIds.push(option.id);
            }
        } else {
            //remove from selected options
            if (option.parent && ['single'].includes(option.parent.kind)) {
                if (ruleTimeSelectedSide !== sides.left) {
                    if (
                        ruleTimeSelectedIds.rightSelectedIds.includes(option.id)
                    ) {
                        ruleTimeSelectedIds.rightSelectedIds.splice(
                            ruleTimeSelectedIds.rightSelectedIds.indexOf(
                                option.id
                            ),
                            1
                        );
                    }
                }
                if (ruleTimeSelectedSide !== sides.right) {
                    if (
                        ruleTimeSelectedIds.leftSelectedIds.includes(option.id)
                    ) {
                        ruleTimeSelectedIds.leftSelectedIds.splice(
                            ruleTimeSelectedIds.leftSelectedIds.indexOf(
                                option.id
                            ),
                            1
                        );
                    }
                }
            }
            if (side === sides.right) {
                if (ruleTimeSelectedIds.rightSelectedIds.includes(option.id)) {
                    ruleTimeSelectedIds.rightSelectedIds.splice(
                        ruleTimeSelectedIds.rightSelectedIds.indexOf(option.id),
                        1
                    );
                }
            }
            if (side === sides.left) {
                if (ruleTimeSelectedIds.leftSelectedIds.includes(option.id)) {
                    ruleTimeSelectedIds.leftSelectedIds.splice(
                        ruleTimeSelectedIds.leftSelectedIds.indexOf(option.id),
                        1
                    );
                }
            }
        }
    }
};

const implementRules = (rule: IActions, side: string) => {
    let result: ChangeResult = {};
    if (rule.activates) {
        rule.activates.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (item) addRemoveFromOptions(item, side, true);
            if (
                item &&
                item.parent &&
                [
                    'select',
                    'select-nd',
                    'single',
                    'single, inline',
                    'dual-single',
                    'dual-single-nd',
                ].includes(item.parent.kind)
            ) {
                if (
                    !findOption(
                        item.parent.id,
                        ruleTimeOptionAvailability
                    )!.items.some((x) => {
                        return isSelected(x.id, side);
                    })
                ) {
                    selectDefaultsForOptionType(item.parent, side, false);
                }
            }
        });
    }
    if (rule.deactivates) {
        rule.deactivates.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (isSelected(x, side)) {
                addRemoveFromSelects(item!, side, false);
                result.changesMade = true;
            }
            addRemoveFromOptions(item!, side, false);
            if (
                item &&
                item.parent &&
                [
                    'select',
                    'select-nd',
                    'dual-select',
                    'dual-select-nd',
                    'single',
                    'single, inline',
                    'dual-single',
                    'dual-single-nd',
                ].includes(item.parent.kind)
            ) {
                if (
                    !findOption(
                        item.parent.id,
                        ruleTimeOptionAvailability
                    )!.items.some((x) => {
                        return isSelected(x.id, side);
                    })
                ) {
                    selectDefaultsForOptionType(item.parent, side, false);
                }
            }
        });
    }
    if (rule.unDeactivates) {
        rule.unDeactivates.forEach((x) => {
            if (isActivatable(x, side)) {
                rule.activates = [];
                rule.unDeactivates = [];
                rule.activates.push(x);
                result = implementRules(rule, side);
                rule.activates = [];
            }
        });
    }
    if (rule.selects) {
        rule.selects.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (isAvailable(item!, side)) {
                if (!isSelected(x, side)) result.changesMade = true;
                addRemoveFromSelects(item!, side, true);
            } else {
                if (item) {
                    result.error = `${
                        item!.name
                    } is required and is not currently selectable`;
                } else {
                    result.error = `required item ${x} is not present`;
                }
            }
        });
    }
    if (rule.prefers && !rule.ignorePrefers) {
        rule.prefers.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (isAvailable(item!, side)) {
                if (!isSelected(x, side)) result.changesMade = true;
                addRemoveFromSelects(item!, side, true);
            }
        });
    }
    if (rule.unselects) {
        rule.unselects.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (isAvailable(item!, side)) {
                if (isSelected(x, side)) result.changesMade = true;
                addRemoveFromSelects(item!, side, false);
            }
        });
    }
    if (rule.reevaluates) {
        rule.reevaluates.forEach((x) => {
            const item: IOption | null = findOption(
                x,
                ruleTimeOptionAvailability
            );
            if (
                isAvailable(item!, side) &&
                (side === sides.left ||
                    orderstateSelectedIds.rightSelectedIds.includes(
                        item!.id
                    )) &&
                (side === sides.right ||
                    orderstateSelectedIds.leftSelectedIds.includes(item!.id))
            ) {
                result.changesMade = true;
                if (side !== sides.left) {
                    orderstateSelectedIds.rightSelectedIds.splice(
                        orderstateSelectedIds.rightSelectedIds.indexOf(
                            item!.id
                        ),
                        1
                    );
                } //reset selected options;
                if (side !== sides.right) {
                    orderstateSelectedIds.leftSelectedIds.splice(
                        orderstateSelectedIds.leftSelectedIds.indexOf(item!.id),
                        1
                    );
                } //reset selected options
            }
        });
    }
    return result;
};

const runRulesSide = (rules: IActions[], side: string) => {
    let result: ChangeResult = { changesMade: false };
    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        if (rule.selected) {
            const item: IOption | null = findOption(
                rule.selected,
                ruleTimeOptionAvailability
            );
            const conditionMet =
                hasChanged(item!, side) && isSelected(rule.selected, side);

            if (conditionMet) {
                result = implementRules(rule, side);
                if (result.error) {
                    return result;
                } else if (result.changesMade) {
                    return result;
                }
            }
        }

        if (rule.unselected) {
            const item = findOption(
                rule.unselected,
                ruleTimeOptionAvailability
            )!;
            if (hasChanged(item, side) && !isSelected(item.id, side)) {
                result = implementRules(rule, side);
                if (result.changesMade) {
                    return result;
                }
            }
        }

        if (rule.incompatible) {
            if (rule.hides) {
                const item = findOption(
                    rule.incompatible,
                    ruleTimeOptionAvailability
                )!;
                if (hasChanged(item, side)) {
                    if (isSelected(item.id, side)) {
                        rule.deactivates = rule.hides;
                        result = implementRules(rule, side);
                        rule.deactivates = [];
                        if (result.changesMade) {
                            return result;
                        }
                    } else {
                        [510, 528, 583, 584, 424, 509, 1298].includes(
                            rule.incompatible
                        )
                            ? (rule.unDeactivates = rule.hides)
                            : (rule.activates = rule.hides);
                        result = implementRules(rule, side);
                        rule.unDeactivates = [];
                        rule.activates = [];
                    }
                }
            }
            if (
                rule.hidesFrom &&
                rule.hidesFrom.some((x) => {
                    const item = findOption(x, ruleTimeOptionAvailability);
                    if (item) {
                        return hasChanged(item, side);
                    } else {
                        return false;
                    }
                })
            ) {
                if (
                    rule.hidesFrom.some((x) => {
                        return isSelected(x, side);
                    })
                ) {
                    rule.deactivates = [];
                    rule.deactivates.push(rule.incompatible);
                    result = implementRules(rule, side);
                    rule.deactivates = [];
                    if (result.changesMade) return result;
                } else {
                    if ([651, 652, 656, 657, 731].includes(rule.incompatible)) {
                        rule.unDeactivates = [];
                        rule.unDeactivates.push(rule.incompatible);
                        result = implementRules(rule, side);
                        rule.unDeactivates = [];
                    } else {
                        rule.activates = [];
                        rule.activates.push(rule.incompatible);
                        result = implementRules(rule, side);
                        rule.activates = [];
                    }
                }
            }
            if (
                rule.showIf &&
                rule.showIf.some((x) => {
                    return hasChanged(
                        findOption(x, ruleTimeOptionAvailability)!,
                        side
                    );
                })
            ) {
                if (
                    rule.showIf.some((x) => {
                        return isSelected(x, side);
                    })
                ) {
                    rule.activates = [];
                    rule.activates.push(rule.incompatible);
                    implementRules(rule, side);
                    rule.activates = [];
                } else {
                    rule.deactivates = [];
                    rule.deactivates.push(rule.incompatible);
                    result = implementRules(rule, side);
                    rule.deactivates = [];
                    if (result.changesMade) return result;
                }
            }
            if (
                rule.requires &&
                rule.requires.some((x) => {
                    return hasChanged(
                        findOption(x, ruleTimeOptionAvailability)!,
                        side
                    );
                })
            ) {
                if (
                    rule.requires.every((x) => {
                        return isSelected(x, side);
                    })
                ) {
                    rule.unDeactivates = [];
                    rule.unDeactivates.push(rule.incompatible);
                    implementRules(rule, side);
                    rule.unDeactivates = [];
                } else {
                    rule.deactivates = [];
                    rule.deactivates.push(rule.incompatible);
                    result = implementRules(rule, side);
                    rule.deactivates = [];
                    if (result.changesMade) return result;
                }
            }
        }
    }

    return result;
};

const runRules = (isEarmold: boolean, sides: sides[]) => {
    if (rules) {
        const error = sides.some((side) => {
            let iteration = 1;
            let result: ChangeResult = { changesMade: true };
            while (result.changesMade) {
                result = runRulesSide(rules, side);
                if (++iteration >= 50) {
                    result.error = 'Error: circular rules detected';
                    alert('circular rules');
                }
                if (result.error) {
                    return true;
                }
            }
            runRulesSide(rules, side);
            return false;
        });
        if (!error) {
            const updatedSelectsandoptions = {
                selectedOptionIds: {
                    rightSelectedIds: ruleTimeSelectedIds.rightSelectedIds,
                    leftSelectedIds: ruleTimeSelectedIds.leftSelectedIds,
                },
                options: ruleTimeOptionAvailability,
            };

            return updatedSelectsandoptions;
        }
    }
};

export const rulesInit = (
    options: IOption[],
    selectedIds: ISelectedOptionIds,
    isEarmold: boolean,
    selectedIdsOnState: ISelectedOptionIds,
    selectedSides: sides[]
) => {
    ruleTimeOptionAvailability = JSON.parse(JSON.stringify(options));

    rules = [];

    extractRules(ruleTimeOptionAvailability, rules);

    ruleTimeSelectedIds = selectedIds;

    orderstateSelectedIds = selectedIdsOnState;

    ruleTimeSelectedSide =
        selectedSides.length > 1
            ? sides.binaural
            : selectedSides.includes(sides.right)
            ? sides.right
            : sides.left;

    const update = runRules(isEarmold, selectedSides);

    return update;
};

export const runEarmoldConditions = (
    selectedIds: ISelectedOptionIds,
    options: IOption[],
    orderState: IOrderDetails
) => {
    ruleTimeOptionAvailability = JSON.parse(JSON.stringify(options));

    rules = [];

    extractRules(ruleTimeOptionAvailability, rules);

    ruleTimeSelectedIds = JSON.parse(JSON.stringify(selectedIds));

    orderstateSelectedIds = {
        rightSelectedIds: selectedIds.rightSelectedIds,
        leftSelectedIds: selectedIds.leftSelectedIds,
    };

    ruleTimeSelectedIds = JSON.parse(
        JSON.stringify(
            syncHearingLoss(
                orderstateSelectedIds,
                ruleTimeOptionAvailability,
                orderState.patient!
            )
        )
    );

    ruleTimeSelectedIds = JSON.parse(
        JSON.stringify(
            showHideEarmolOptionsBasedOnCROS(orderState, ruleTimeSelectedIds)
        )
    );

    let actionObject: IActions;

    ////disable parallel option for monaural cros
    if (orderState.cros && orderState.side !== 'B') {
        [sides.right, sides.left].map((x) =>
            implementRules({ ...actionObject, deactivates: [424, 509] }, x)
        );
    }

    ///disable angled receivers for monaurak cros
    if (orderState.cros && orderState.side !== 'B') {
        [sides.right, sides.left].map((x) =>
            implementRules({ ...actionObject, deactivates: [718, 719] }, x)
        );
    }

    return runRules(true, [sides.right, sides.left]);
};

export const syncHearingLoss = (
    selectedIds: ISelectedOptionIds,
    options: IOption[],
    patient: IPatient
) => {
    const lossTypeOptions = findOption(38, options);
    const materialOptions = findOption(30, options);

    ruleTimeSelectedIds = JSON.parse(JSON.stringify(selectedIds));

    if (lossTypeOptions && patient!.rightLoss && patient!.leftLoss) {
        ruleTimeSelectedIds = handleOptionSelection(
            ruleTimeSelectedIds,
            lossTypeOptions,
            findOption(
                ['Mild', 'Moderate', 'Severe', 'Profound'][
                    patient!.rightLoss.loss!.id - 1
                ],
                lossTypeOptions.items
            )!.id,
            sides.right
        );

        ruleTimeSelectedIds = handleOptionSelection(
            ruleTimeSelectedIds,
            lossTypeOptions,
            findOption(
                ['Mild', 'Moderate', 'Severe', 'Profound'][
                    patient!.leftLoss.loss!.id - 1
                ],
                lossTypeOptions.items
            )!.id,
            sides.left
        );

        orderstateSelectedIds = JSON.parse(
            JSON.stringify(
                runRules(true, [sides.right, sides.left])!.selectedOptionIds
            )
        );
    }

    if (materialOptions && patient!.rightLoss && patient!.leftLoss) {
        let material = '';

        if (
            Math.max(
                patient!.rightLoss.loss!.id,
                patient!.leftLoss.loss!.id
            ) === 4
        ) {
            material = 'Soft';
        } else {
            material = 'Hard';
        }

        const materialOption = findOption(material, materialOptions.items);
        if (materialOption) {
            ruleTimeSelectedIds = handleOptionSelection(
                ruleTimeSelectedIds,
                materialOptions,
                materialOption.id,
                sides.binaural
            );
            orderstateSelectedIds = JSON.parse(
                JSON.stringify(
                    runRules(true, [sides.right, sides.left])!.selectedOptionIds
                )
            );
        }
    }

    return ruleTimeSelectedIds;
};

export const showHideEarmolOptionsBasedOnCROS = (
    orderState: IOrderDetails,
    selectedEarmoldIds: ISelectedOptionIds
) => {
    const tempright = JSON.parse(
        JSON.stringify(selectedEarmoldIds.rightSelectedIds)
    );
    const templeft = JSON.parse(
        JSON.stringify(selectedEarmoldIds.leftSelectedIds)
    );
    if (orderState.rightSelectedIds.includes(510)) {
        if (!tempright.includes(528)) {
            tempright.push(528);
        }

        if (templeft.includes(528)) templeft.splice(templeft.indexOf(528), 1);
    }

    if (orderState.leftSelectedIds.includes(510)) {
        if (!templeft.includes(528)) {
            templeft.push(528);
        }
        if (tempright.includes(528))
            tempright.splice(tempright.indexOf(528), 1);
    }

    /// setting earmold for cros to 'Yes' because earmold options will be loaded only if CROS transmitter is selected and 'Yes' is selected for outside question
    if (
        orderState.cros &&
        orderState.side !== 'B' &&
        (orderState.rightSelectedIds.includes(510) ||
            orderState.leftSelectedIds.includes(510))
    ) {
        if (!tempright.includes(599)) {
            tempright.push(599);
        }

        if (!templeft.includes(599)) {
            templeft.push(599);
        }
    }

    return {
        rightSelectedIds: tempright,
        leftSelectedIds: templeft,
    } as ISelectedOptionIds;
};

const handleOptionSelection = (
    selectedIds: ISelectedOptionIds,
    option: IOption,
    id: number,
    side: sides
) => {
    const right: number[] = JSON.parse(
        JSON.stringify(selectedIds.rightSelectedIds)
    );
    const left: number[] = JSON.parse(
        JSON.stringify(selectedIds.leftSelectedIds)
    );
    option.items.map((x) => {
        switch (side) {
            case sides.right:
                if (x.id === id) {
                    if (!right.includes(id)) right.push(id);
                } else {
                    if (right.indexOf(x.id) >= 0)
                        right.splice(right.indexOf(x.id), 1);
                }
                break;
            case sides.left:
                if (x.id === id) {
                    if (!left.includes(id)) left.push(id);
                } else {
                    if (left.indexOf(x.id) >= 0)
                        left.splice(left.indexOf(x.id), 1);
                }
                break;
            case sides.binaural:
                if (x.id === id) {
                    if (!right.includes(id)) right.push(id);
                    if (!left.includes(id)) left.push(id);
                } else {
                    if (right.indexOf(x.id) >= 0)
                        right.splice(right.indexOf(x.id), 1);
                    if (left.indexOf(x.id) >= 0)
                        left.splice(left.indexOf(x.id), 1);
                }
        }
        return null;
    });

    return {
        rightSelectedIds: right,
        leftSelectedIds: left,
    } as ISelectedOptionIds;
};

//#endregion rules

export const fetchEarmoldOptions = async (
    catalog: number,
    earmoldDesignId: number,
    session: ISession,
    productDetails: IProductDetail
) => {
    const options = await getEarmoldOptions(
        catalog,
        earmoldDesignId,
        productDetails.isSnapFit2
    );

    const earmoldOptions = loadEarmoldOptionsAndRules(
        options!.data!,
        session,
        productDetails
    );

    return earmoldOptions;
};

export const loadEarmoldOptionsAndRules = (
    options: IOption[],
    session: ISession,
    productDetails: IProductDetail
) => {
    options.forEach((optionCategory) => {
        /////Write multiple condition here ot remove options ----- refer to old code
        if (optionCategory.groups!.length > 0) {
            if (productDetails!.cros) {
                optionCategory.items = optionCategory.groups!.filter(
                    (x) => ![34].includes(x.id) // remove non-CROS Earmold tubing options
                );
            } else {
                optionCategory.items = optionCategory.groups!.filter(
                    (x) => ![85, 87, 90, 91].includes(x.id) // remove CROS Earmold tubing group options
                );
            }
            optionCategory.groups = undefined;

            ///setting the CROS question on earmold options
            if (optionCategory.items.find((x) => x.id === 85)) {
                optionCategory.name = 'Earmold for CROS Transmitter?';
            }

            optionCategory.items.forEach((optiontype) => {
                // TODO: remove 40 gain from snapfit items
                if (
                    ['US', 'CA', 'AU', 'NZ'].includes(session!.country) &&
                    productDetails!.isSnapfit &&
                    productDetails!.cartItemTypeId ===
                        CartItemTypes.RicWithEarmold
                ) {
                    const invalidGain = optiontype.options!.find((x) => {
                        return x.name === '40 dB';
                    });
                    if (invalidGain) {
                        optiontype.options!.splice(
                            optiontype.options!.findIndex(
                                (x) => x.name === '40 dB'
                            ),
                            1
                        );
                        /////some fitting logic omited becasue no fitting on new PDP
                    }
                }
                optiontype.kind = optionKinds[optiontype.groupType!];
                optiontype.items = optiontype.options!;
                optiontype.items!.forEach((option) => {
                    if (
                        [
                            621,
                            622,
                            623,
                            624,
                            625,
                            626,
                            627,
                            628,
                            648,
                            649,
                            650,
                            651,
                            652,
                            841,
                            674, // Tubing
                            601, // CROS Vent
                            957,
                        ].includes(option.id)
                    ) {
                        option.isAvailableLeft = false;
                        option.isAvailableRight = false;
                    } else {
                        option.isAvailableLeft = true;
                        option.isAvailableRight = true;
                    }
                    if (optiontype.kind) {
                        let optionObject: IOption;
                        option.parent = {
                            ...optionObject!,
                            kind: optiontype.kind,
                            id: optiontype.id,
                            name: optiontype.name,
                        };
                    }
                });

                optiontype.options = undefined;
            });
        }
    });
    return options;
};
